import { useLoad } from "@redotech/react-util/load";
import TableIcon from "@redotech/redo-icons/table.svg";
import XCloseIcon from "@redotech/redo-icons/x-close.svg";
import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { AbTestStep } from "@redotech/redo-model/advanced-flow/advanced-flow-db-parser";
import { StepType } from "@redotech/redo-model/advanced-flow/steps";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { LabelText } from "@redotech/redo-web/arbiter-components/common/descriptors";
import { RedoEmptyState } from "@redotech/redo-web/arbiter-components/empty-state/redo-empty-state";
import { MetricItemHelpIcon } from "@redotech/redo-web/arbiter-components/metric-item/redo-metric-item";
import { RedoHorizontalTabs } from "@redotech/redo-web/arbiter-components/tabs/redo-horizontal-tabs";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { ObjectId } from "bson";
import { useCallback, useContext, useMemo, useState } from "react";
import { FlowInProgress } from "../flow-editor";
import {
  FrontendStepType,
  PartialFlowStep,
  PartialWithRequiredType,
} from "../steps";
import { deleteElementAndAllChildren } from "../utils/delete-element";
import { ABTestReportModal } from "./ab-test-report-modal";
import {
  SplitVariantControls,
  SplitVariantControlsProps,
} from "./common/split-variant-controls";

const TABS = ["variations", "report"]; // Can't add `const` because HorizontalTabs needs it to be mutable for some reason
type Tab = (typeof TABS)[number];

// Interfaces for variant metrics
export interface VariantInfo {
  id: string;
  name: string;
  nodeId?: string;
  isEmail?: boolean;
  weight: number;
  color: number;
}

export interface VariantMetrics {
  variant: VariantInfo;
  openRate: number;
  clickRate: number;
  conversionRate: number;
  revenue: number;
  metrics: any;
}

interface TopPerformers {
  openRate: VariantMetrics | null;
  clickRate: VariantMetrics | null;
  conversionRate: VariantMetrics | null;
  revenue: VariantMetrics | null;
}

// Function to find the first send message node (email or SMS) in a variant branch
function findFirstSendNodeInBranch(
  nextId: string | undefined,
  steps: PartialFlowStep[],
): { nodeId: string; isEmail: boolean } | null {
  if (!nextId) {
    return null;
  }

  // Find the step with the given ID
  const currentStep = steps.find((step) => step.id === nextId);
  if (!currentStep) {
    return null;
  }

  // Check if this is a send message node
  if (currentStep.type === StepType.SEND_EMAIL) {
    return { nodeId: currentStep.id, isEmail: true };
  } else if (currentStep.type === StepType.SEND_SMS) {
    return { nodeId: currentStep.id, isEmail: false };
  }

  // Continue searching in the next step
  if ("nextId" in currentStep && currentStep.nextId) {
    return findFirstSendNodeInBranch(currentStep.nextId, steps);
  }

  // Handle branches in conditional steps
  if ("nextTrueId" in currentStep && currentStep.nextTrueId) {
    const trueBranch = findFirstSendNodeInBranch(currentStep.nextTrueId, steps);
    if (trueBranch) {
      return trueBranch;
    }
  }

  if ("nextFalseId" in currentStep && currentStep.nextFalseId) {
    const falseBranch = findFirstSendNodeInBranch(
      currentStep.nextFalseId,
      steps,
    );
    if (falseBranch) {
      return falseBranch;
    }
  }

  return null;
}

// Function to map variants to their first send node info
function getVariantInfos(
  variants: PartialWithRequiredType<AbTestStep>["variants"],
  steps: PartialFlowStep[],
  stepId: string | ObjectId | undefined,
): VariantInfo[] {
  if (!variants) {
    return [];
  }

  return variants.map((variant, index) => {
    const variantLetter = String.fromCharCode(65 + index); // A, B, C, etc.
    const variantName = variant?.name || `Variant ${variantLetter}`;

    // Find the first send node in this variant's branch
    const sendNode = variant?.nextId
      ? findFirstSendNodeInBranch(variant.nextId, steps)
      : null;

    return {
      id: variant?.id || "",
      name: variantName,
      nodeId: sendNode?.nodeId,
      isEmail: sendNode?.isEmail,
      weight: variant?.weight || 0,
      color: index < 6 ? index + 1 : 1, // Map to color classes 1-6
    };
  });
}

// Function to get top performers from metrics data
function findTopPerformers(
  variantMetricsData: VariantMetrics[],
): TopPerformers {
  if (variantMetricsData.length === 0) {
    return {
      openRate: null,
      clickRate: null,
      conversionRate: null,
      revenue: null,
    };
  }

  // Find top performer for each metric - if there's a tie, the first one will be used
  const openRateTop =
    [...variantMetricsData].sort((a, b) => b.openRate - a.openRate)[0] || null;
  const clickRateTop =
    [...variantMetricsData].sort((a, b) => b.clickRate - a.clickRate)[0] ||
    null;
  const conversionRateTop =
    [...variantMetricsData].sort(
      (a, b) => b.conversionRate - a.conversionRate,
    )[0] || null;
  const revenueTop =
    [...variantMetricsData].sort((a, b) => b.revenue - a.revenue)[0] || null;

  return {
    openRate: openRateTop,
    clickRate: clickRateTop,
    conversionRate: conversionRateTop,
    revenue: revenueTop,
  };
}

// Reusable metric card component for displaying performance metrics
interface MetricCardProps {
  title: string;
  data: VariantMetrics | null;
  metricKey: keyof Pick<
    VariantMetrics,
    "openRate" | "clickRate" | "conversionRate" | "revenue"
  >;
  format: (value: number) => string;
  emptyValue: string;
  tooltipTitle: string;
  tooltipDescription: string;
}

const MetricCard = ({
  title,
  data,
  metricKey,
  format,
  emptyValue,
  tooltipTitle,
  tooltipDescription,
}: MetricCardProps) => {
  return (
    <Card
      headerExtra={
        <MetricItemHelpIcon
          tooltipProps={{
            title: tooltipTitle,
            metricDescription: tooltipDescription,
          }}
        />
      }
      subtitle={
        data ? (
          <Flex align="flex-end" gap="sm" wrap="wrap">
            <Text fontSize="2xl" fontWeight="bold">
              {format(data[metricKey])}
            </Text>
            <Text pb="xs">{data.variant.name}</Text>
          </Flex>
        ) : (
          <Text>{emptyValue}</Text>
        )
      }
      title={
        <Text fontSize="sm" textColor="tertiary">
          {title}
        </Text>
      }
    />
  );
};

export const AbTestInspector = ({
  step,
  onClose,
  onChange,
  setAutomation,
  steps,
  automationId,
}: {
  step: PartialWithRequiredType<AbTestStep>;
  onClose?: () => void;
  onChange?: (newStep: PartialWithRequiredType<AbTestStep>) => void;
  setAutomation?: React.Dispatch<React.SetStateAction<FlowInProgress | null>>;
  steps: PartialFlowStep[];
  automationId?: string;
}) => {
  const [selectedTab, setSelectedTab] = useState<Tab>("variations");
  const marketingRpcClient = useContext(RedoMarketingRpcClientContext);
  const team = useContext(TeamContext);

  const variants: Partial<AbTestStep["variants"][number]>[] = useMemo(() => {
    return step.variants ?? [];
  }, [step.variants]);

  const totalWeight = useMemo(() => {
    return variants.reduce((acc, variant) => acc + (variant.weight || 0), 0);
  }, [variants]);

  const isWeightValid: SplitVariantControlsProps["isWeightValid"] =
    useMemo(() => {
      if (totalWeight > 100) {
        return {
          isValid: false,
          errorMessage: "Variations exceed 100%",
          errorLevel: "error",
        };
      }
      if (totalWeight < 100) {
        return {
          isValid: false,
          errorMessage: "Variations less than 100%",
          errorLevel: "warning",
        };
      }
      return { isValid: true };
    }, [totalWeight]);

  // Get automation data from props
  const flowSteps = steps;

  // Get variant infos with their send nodes
  const variantInfos = useMemo(() => {
    return getVariantInfos(variants, flowSteps, step.id);
  }, [variants, flowSteps, step.id]);

  // Create a string key to use for metrics loading
  const variantNodeKeys = useMemo(() => {
    return variantInfos
      .map((variant) => variant.nodeId)
      .filter(Boolean)
      .join(",");
  }, [variantInfos]);

  // Load all metrics in a single useLoad hook
  const allMetricsLoad = useLoad(async () => {
    if (!marketingRpcClient || !automationId) {
      return [];
    }

    const results: VariantMetrics[] = [];

    // Process each variant with a send node
    for (const variant of variantInfos) {
      if (!variant.nodeId) {
        continue;
      }

      try {
        // Use the automationId from the FlowInProgress context
        const metrics = await marketingRpcClient.getOutreachAnalytics({
          groupBy: {
            automation: automationId,
            automationStepId: variant.nodeId,
          },
        });

        const sends = metrics.sends || 0;
        const deliveries = metrics.deliveries || 0;
        const opens = metrics.uniqueOpens || 0;
        const clicks = metrics.uniqueClicks || 0;
        const upsells = metrics.upsellCount || 0;
        const revenue = metrics.upsellRevenue || 0;

        // Calculate rates correctly
        const openRate = sends > 0 ? (opens / sends) * 100 : 0;
        const clickRate = sends > 0 ? (clicks / sends) * 100 : 0;

        // Conversion rate is the percentage of deliveries that resulted in upsells (conversions)
        let conversionRate = 0;
        if (deliveries > 0) {
          conversionRate = (upsells / deliveries) * 100;
        }

        results.push({
          variant,
          openRate,
          clickRate,
          conversionRate,
          revenue,
          metrics,
        });
      } catch (error) {
        console.error(
          `Failed to load metrics for variant ${variant.name}:`,
          error,
        );
      }
    }

    return results;
  }, [
    marketingRpcClient,
    automationId,
    variantNodeKeys,
    team?.settings.marketing?.enabled,
  ]);

  // Get all loaded metrics data
  const metricsData = useMemo(() => {
    return allMetricsLoad.value || [];
  }, [allMetricsLoad.value]);

  // Find top performers for each metric
  const topPerformers = useMemo(() => {
    return findTopPerformers(metricsData);
  }, [metricsData]);

  const handleVariantNameChange = useCallback(
    (id: string, name: string) => {
      if (onChange && variants) {
        const updatedVariants = variants.map((variant) =>
          variant.id === id ? { ...variant, name } : variant,
        );

        onChange({ ...step, variants: updatedVariants });
      }
    },
    [onChange, step, variants],
  );

  const handleVariantWeightChange = useCallback(
    (id: string, weightStr: string) => {
      if (onChange && variants) {
        const weight = parseInt(weightStr, 10) || 0;

        const updatedVariants = variants.map((variant) =>
          variant.id === id ? { ...variant, weight } : variant,
        );

        onChange({ ...step, variants: updatedVariants });
      }
    },
    [onChange, step, variants],
  );

  const handleAddVariant = useCallback(() => {
    // IMPORTANT: Don't use setAutomation and onChange at the same time.
    // Previously, calling both onChange() and setAutomation() caused race conditions where
    // one update would override the other, preventing the AB test step from showing new variants.
    // There is probably some better way to refactor this, but for now just do all the changes in one big atomic update.
    if (setAutomation) {
      const letter = String.fromCharCode(65 + variants.length); // A, B, C, etc.
      const variantId = new ObjectId().toString();

      // Create a unique endpoint ID for this variant - this will become a DO_NOTHING node
      const newEndpointId = new ObjectId().toString();

      const newVariant = {
        id: variantId,
        name: `Variant ${letter}`,
        weight: 0,
        nextId: newEndpointId, // Each variant gets its own unique endpoint
      };

      const updatedVariants = [...variants, newVariant];
      const updatedStep = { ...step, variants: updatedVariants };

      const doNothingNode: PartialFlowStep = {
        id: newEndpointId,
        type: StepType.DO_NOTHING,
      };

      setAutomation((prevAutomation) => {
        if (!prevAutomation) {
          return prevAutomation;
        }

        // Update the AB test step in the steps array
        const newSteps = prevAutomation.steps.map((s) =>
          s.id === step.id ? updatedStep : s,
        );

        newSteps.push(doNothingNode);

        return { ...prevAutomation, steps: newSteps };
      });
    }
  }, [setAutomation, step, variants]);

  const handleRemoveVariant = useCallback(
    (id: string) => {
      if (onChange && variants) {
        // Find the variant that's being removed
        const variantToRemove = variants.find((variant) => variant.id === id);

        // Update the variants list in the AB test step
        const updatedVariants = variants.filter((variant) => variant.id !== id);

        onChange({ ...step, variants: updatedVariants });

        // If we have setAutomation and the variant has a nextId, delete all connected nodes
        if (setAutomation && variantToRemove?.nextId) {
          setAutomation((prevAutomation) => {
            if (!prevAutomation) {
              return prevAutomation;
            }

            // Use deleteElementAndAllChildren to remove the variant's branch
            const updatedSteps = deleteElementAndAllChildren(
              variantToRemove.nextId as string,
              prevAutomation.steps,
            );

            return { ...prevAutomation, steps: updatedSteps };
          });
        }
      }
    },
    [onChange, step, variants, setAutomation],
  );

  const handleBalanceWeights = useCallback(() => {
    if (onChange && variants && variants.length > 0) {
      const equalWeight = Math.floor(100 / variants.length);
      const remainder = 100 - equalWeight * variants.length;

      const updatedVariants = variants.map((variant, index) => ({
        ...variant,
        weight: equalWeight + (index < remainder ? 1 : 0),
      }));

      onChange({ ...step, variants: updatedVariants });
    }
  }, [onChange, step, variants]);

  // State for controlling the report modal
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  // Function to open the report modal
  const handleOpenReportModal = useCallback(() => {
    setIsReportModalOpen(true);
  }, []);

  // Function to close the report modal
  const handleCloseReportModal = useCallback(() => {
    setIsReportModalOpen(false);
  }, []);

  return (
    <Flex dir="column" gap="3xl" justify="flex-start">
      <Flex dir="row" justify="space-between">
        <Text fontSize="md" fontWeight="semibold">
          Weighted branch
        </Text>
        <RedoButton
          hierarchy="tertiary"
          IconLeading={XCloseIcon}
          onClick={onClose}
          size="sm"
        />
      </Flex>

      <RedoHorizontalTabs<Tab>
        drawBottomLine
        pl="none"
        selectedTab={selectedTab}
        setSelectedTab={setSelectedTab}
        tabs={TABS.map((tab) => ({
          key: tab,
          label: tab.charAt(0).toUpperCase() + tab.slice(1),
        }))}
      />

      {selectedTab === "variations" && (
        <SplitVariantControls
          isWeightValid={isWeightValid}
          onAddVariant={handleAddVariant}
          onBalanceWeights={handleBalanceWeights}
          onRemoveVariant={handleRemoveVariant}
          onVariantNameChange={handleVariantNameChange}
          onVariantWeightChange={handleVariantWeightChange}
          totalWeight={totalWeight}
          variants={variants.map((variant) => ({
            id: variant.id || "",
            name: variant.name || "",
            weight: variant.weight || 0,
          }))}
        />
      )}
      {selectedTab === "report" && (
        <Flex dir="column" gap="lg" justify="flex-start">
          <Flex align="center" justify="space-between">
            <LabelText label="Top performers" size="xs" />
            <RedoButton
              hierarchy="secondary"
              onClick={handleOpenReportModal}
              text="View full report"
            />
          </Flex>

          {allMetricsLoad.pending ? (
            <RedoEmptyState
              Icon={TableIcon}
              subtext="Fetching metrics data for all variants."
              text="Loading..."
            />
          ) : metricsData.length === 0 ? (
            <RedoEmptyState
              Icon={TableIcon}
              subtext="Variants need to have email or SMS nodes to collect metrics."
              text="No metrics available"
            />
          ) : (
            <>
              <MetricCard
                data={topPerformers.openRate}
                emptyValue="0%"
                format={(value) => `${value.toFixed(1)}%`}
                metricKey="openRate"
                title="Open rate"
                tooltipDescription="Percentage of total messages sent that were opened by recipients (messages opened ÷ messages sent × 100)"
                tooltipTitle="Open rate (email only)"
              />
              <MetricCard
                data={topPerformers.clickRate}
                emptyValue="0%"
                format={(value) => `${value.toFixed(1)}%`}
                metricKey="clickRate"
                title="Click rate"
                tooltipDescription="Percentage of sent messages that received at least one click on a link (clicks ÷ messages sent × 100)"
                tooltipTitle="Click rate"
              />
              <MetricCard
                data={topPerformers.conversionRate}
                emptyValue="0%"
                format={(value) => `${value.toFixed(1)}%`}
                metricKey="conversionRate"
                title="Conversion rate"
                tooltipDescription="Percentage of contacts who completed a desired action like making a purchase or signing up (conversions ÷ total contacts × 100)"
                tooltipTitle="Conversion Rate"
              />
              <MetricCard
                data={topPerformers.revenue}
                emptyValue="$0"
                format={(value) => `$${value.toFixed(2)}`}
                metricKey="revenue"
                title="Revenue"
                tooltipDescription="Total dollar amount generated from this automation, typically calculated from completed purchases or transactions"
                tooltipTitle="Revenue"
              />
            </>
          )}
        </Flex>
      )}

      {/* Weighted branch Report Modal */}
      <ABTestReportModal
        isLoading={allMetricsLoad.pending}
        isOpen={isReportModalOpen}
        metricsData={metricsData}
        onClose={handleCloseReportModal}
        variants={variantInfos}
      />
    </Flex>
  );
};

export const abTestStepType: FrontendStepType<
  PartialWithRequiredType<AbTestStep>
> = {
  type: "custom",
  valid: (state) => {
    if (!state.variants || state.variants.length === 0) {
      return false;
    }

    const totalWeight = state.variants.reduce(
      (acc, variant) => acc + (variant.weight ?? 0),
      0,
    );

    return totalWeight === 100;
  },
  selectable: () => true,
  customInspector: (args) => {
    return AbTestInspector({
      step: args.step,
      onChange: args.setStep,
      onClose: args.onClose,
      setAutomation: args.setAutomation,
      steps: args.steps,
      automationId: args.automationId,
    });
  },
};
